import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@design-system/Badge';
import { Image } from '@design-system/Image';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@design-system/Dialog';
import { SMPDialogContent } from '../components';
import { cn } from '@/utils/styles';
import { SMP_DISCOUNT_CODE_PATH } from '@/constants/paths';
import { useSMPNavigate } from '../hooks/useSMPNavigate';
import type { SMPSubscriptionDetail, SMPPlanComponent, SMPSubscriptionLineItem, DiscountsResponse } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  getStatusBadgeClassName,
  getStatusDisplayLabel,
  formatPriceFromCents,
  formatSMPDate,
  isSubscriptionPaused,
  getComponentSupplyLabel,
  formatProductName,
  getProductGradientClass,
  getProductImageForSMP,
  computeDiscountBreakdown,
  taxDollarsToCents,
  getComponentOldSupplyLabel,
} from '../utils/smpHelpers';
const COLLAPSED_PRODUCT_COUNT = 2;

interface SMPLandingPageHeaderProps {
  subscription: SMPSubscriptionDetail;
  productComponents: SMPPlanComponent[];
  customerDiscount: DiscountsResponse | null;
  customerDiscounts?: DiscountsResponse[];
}

const renderProductImage = (title: string, image: string) => {
  return getProductImageForSMP(title).length > 0 ? getProductImageForSMP(title) : image;
};

const ProductComponentRow = ({
  component,
  supplyText,
}: {
  component: SMPPlanComponent;
  supplyText?: string | null;
}) => (
  <div
    className="flex items-center gap-5"
    data-testid={`smp-header-productItem-${component.product_id}`}
  >
    <div
      className={cn(
        "relative size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0",
        getProductGradientClass(component.title),
      )}
    >
      <Image
        src={renderProductImage(component.title, component.image)}
        alt={component.title}
        className="object-contain size-10"
      />
    </div>
    <div className="flex flex-col gap-2">
      <span
        className="typography-body text-secondary"
        data-testid={`smp-header-productName-${component.product_id}`}
      >
        {formatProductName(component.title)}
      </span>
      <span
        className="typography-caption2 text-secondary"
        data-testid={`smp-header-productSupply-${component.product_id}`}
      >
        {supplyText ?? getComponentSupplyLabel(component)}
      </span>
    </div>
  </div>
);

// Fallback for subscriptions with no change-plan components (e.g. a test-kit
// subscription) — the product name is already the card title, so show only the
// line item's image.
const LineItemImage = ({ item }: { item: SMPSubscriptionLineItem }) => (
  <div
    className={cn(
      'relative size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0',
      getProductGradientClass(item.title),
    )}
    data-testid={`smp-header-lineItem-${item.id}`}
  >
    <Image
      src={renderProductImage(item.title, item.image)}
      alt={item.title}
      className="object-cover size-full"
    />
  </div>
);

export const SMPLandingPageHeader = ({
  subscription,
  productComponents,
  customerDiscount,
  customerDiscounts: customerDiscountsProp,
}: SMPLandingPageHeaderProps) => {
  const { line_items, subscription_status, resume_date } = subscription;
  const [isExpanded, setIsExpanded] = useState(false);
  const { withSubId } = useSMPNavigate();

  const totalPriceCents = line_items.reduce(
    (sum, item) => sum + item.price,
    0,
  );

  const allDiscounts: DiscountsResponse[] = customerDiscountsProp
    ?? (customerDiscount ? [customerDiscount] : []);
  const hasDiscount = allDiscounts.length > 0;

  const discountBreakdown = computeDiscountBreakdown(allDiscounts, totalPriceCents);
  const totalDiscountCents = discountBreakdown.reduce((sum, d) => sum + d.amountCents, 0);

  const discountedPriceCents = Math.max(0, totalPriceCents - totalDiscountCents);
  const savingsCents = totalPriceCents - discountedPriceCents;

  const taxCents = taxDollarsToCents(subscription.transaction_details?.tax ?? 0);
  const hasTax = taxCents > 0;
  const displayedNoDiscountPriceCents = hasTax ? totalPriceCents + taxCents : totalPriceCents;
  const displayedDiscountedPriceCents = hasTax ? discountedPriceCents + taxCents : discountedPriceCents;
  const priceSuffix = hasTax ? '/ month (incl. tax)' : '/ month + tax';

  const discountTags = allDiscounts.map((d) => {
    const val = parseFloat(d.discountValue);
    const label = d.discountType === 'percentage'
      ? `${Math.round(val)}% off`
      : `$${val.toFixed(0)} off`;
    return { label: `${label} · ${d.discountCodeName}`, code: d.discountCodeName };
  });

  const planName =
    line_items.length > 0 ? line_items[0].product_name : 'Subscription Plan';

  const isPaused = isSubscriptionPaused(subscription_status);

  const hasComponents = productComponents.length > 0;
  const isCollapsible = hasComponents && productComponents.length > COLLAPSED_PRODUCT_COUNT;
  const visibleComponents = isCollapsible && !isExpanded
    ? productComponents.slice(0, COLLAPSED_PRODUCT_COUNT)
    : productComponents;

  return (
    <Card data-testid="smp-header-card">
      <CardHeader data-testid="smp-header-cardHeader">
        <CardTitle
          className="flex flex-row items-center justify-between typography-h2 text-primary"
          data-testid="smp-header-cardTitle"
        >
          <span>{planName}</span>
          <Badge
            size="sm"
            className={cn(
              'capitalize',
              getStatusBadgeClassName(subscription_status),
            )}
            data-testid="smp-header-statusBadge"
          >
            {getStatusDisplayLabel(subscription_status)}
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent data-testid="smp-header-cardContent">
        <div className="flex flex-col gap-6">
          {/* Resume date info when paused — shown at top of card content */}
          {isPaused && resume_date && (
            <div
              className="flex items-center gap-1"
              data-testid="smp-header-resumeDate"
            >
              <span className="typography-headline3 text-primary">
                Your subscription will resume on {formatSMPDate(resume_date)}.
              </span>
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="inline-flex items-center justify-center size-5 rounded-full text-tertiary hover:text-secondary transition-colors"
                    data-testid="smp-header-resumeDateInfo"
                    aria-label="Resume date information"
                  >
                    <InfoIcon className="size-4" />
                  </button>
                </DialogTrigger>
                <SMPDialogContent>
                  <DialogHeader className="gap-6 text-start">
                    <DialogTitle className="typography-h3 text-primary">
                      Resume Date
                    </DialogTitle>
                    <DialogDescription className="typography-body text-secondary">
                      This is the date your subscription will resume and you
                      will be charged for your next order. You can change the
                      resume date under &ldquo;<span className="text-success">Manage Subscription</span>&rdquo;.
                    </DialogDescription>
                  </DialogHeader>
                </SMPDialogContent>
              </Dialog>
            </div>
          )}

          {/* Price at top when no discount */}
          {!hasDiscount && (
            <div
              className="flex items-end gap-0.5"
              data-testid="smp-header-price"
            >
              <span className="typography-body text-primary">
                {formatPriceFromCents(displayedNoDiscountPriceCents)}
              </span>
              <span className="typography-caption1 text-primary">
                {priceSuffix}
              </span>
            </div>
          )}

          {/* Individual product list */}
          {hasComponents && (
            <div
              className="flex flex-col gap-2"
              data-testid="smp-header-productList"
            >
              <div className="flex flex-col gap-4">
                {visibleComponents.map((component) => (
                  <ProductComponentRow
                    key={component.product_id}
                    component={component}
                    supplyText={getComponentOldSupplyLabel(component)}
                  />
                ))}
              </div>

              {isCollapsible && (
                <button
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="flex items-center justify-center gap-1 py-2 typography-body text-primary cursor-pointer"
                  data-testid="smp-header-toggleProducts"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                  <ChevronRightIcon
                    className={cn(
                      'size-6 transition-transform',
                      isExpanded ? '-rotate-90' : 'rotate-90',
                    )}
                  />
                </button>
              )}
            </div>
          )}

          {/* No change-plan components (e.g. test kit) — show the line-item image only (name is the title) */}
          {!hasComponents && line_items.length > 0 && (
            <div
              className="flex flex-wrap gap-4"
              data-testid="smp-header-lineItemList"
            >
              {line_items.map((item) => (
                <LineItemImage key={item.id} item={item} />
              ))}
            </div>
          )}

          {/* Price + discount info when discount is active — clickable to discount page */}
          {hasDiscount && (
            <Link
              to={withSubId(SMP_DISCOUNT_CODE_PATH)}
              className="group flex flex-col gap-3 rounded-2xl bg-[#faf7f2] p-5 no-underline transition-shadow hover:shadow-md hover:ring-1 hover:ring-black/5 cursor-pointer"
              data-testid="smp-header-discountedPrice"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <span className="text-[32px] font-bold leading-[1.3] text-primary">
                    {formatPriceFromCents(displayedDiscountedPriceCents)}
                  </span>
                  <span className="typography-caption1 text-primary">
                    {priceSuffix}
                  </span>
                  <span className="typography-body text-tertiary line-through">
                    {formatPriceFromCents(displayedNoDiscountPriceCents)}
                  </span>
                </div>
                <ChevronRightIcon className="size-5 shrink-0 text-tertiary transition-transform group-hover:translate-x-0.5" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="typography-caption2 text-tertiary">
                  Total for {line_items.length > 1 ? 'both products' : 'all products'}, billed monthly
                </span>
                <div className="flex flex-col items-end gap-1">
                  {discountTags.map(({ label, code }) => (
                    <Badge
                      key={code}
                      size="sm"
                      className="border border-success/20 bg-transparent text-success"
                      data-testid={`smp-header-discountBadge-${code}`}
                    >
                      {label}
                    </Badge>
                  ))}
                  {savingsCents > 0 && (
                    <span className="typography-caption2 text-success" data-testid="smp-header-savings">
                      You save <span className="font-bold">{formatPriceFromCents(savingsCents)}</span> this month
                    </span>
                  )}
                </div>
              </div>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
